/**
 * BHI Dashboard — Redux slice.
 *
 * Owns filter state for the BHI dashboard: date range + preset id,
 * selected accounts, and account status.
 */
import { createSlice } from '@reduxjs/toolkit';
import { PRESET_IDS, buildPresetRange } from '@entities/bhi-dashboard/lib/buildPresetRange';

const initialRange = () =>
  buildPresetRange(PRESET_IDS.previousMonth) || {
    startDate: null,
    endDate: null,
  };

const initialState = {
  range: initialRange(),
  preset: PRESET_IDS.previousMonth,
  selectedAccounts: [],
  accountStatus: 'active',
  exceptionPreset: null,
};

const bhiDashboardSlice = createSlice({
  name: 'bhiDashboard',
  initialState,
  reducers: {
    setBhiRange: (state, { payload }) => {
      state.range = payload;
    },
    setBhiPreset: (state, { payload }) => {
      state.preset = payload;
      // Mirror CM's setClaimsManagerPreset: a preset IS a range choice,
      // so recompute the range here; 'custom' returns null and leaves
      // the manually picked range untouched.
      const range = buildPresetRange(payload);
      if (range) state.range = range;
    },
    setBhiSelectedAccounts: (state, { payload }) => {
      state.selectedAccounts = payload;
    },
    setBhiAccountStatus: (state, { payload }) => {
      state.accountStatus = payload;
    },
    setBhiExceptionPreset: (state, { payload }) => {
      // Picking the already-active preset toggles it off.
      state.exceptionPreset = state.exceptionPreset === payload ? null : payload || null;
    },
    resetBhiFilters: () => ({ ...initialState, range: initialRange() }),
  },
});

export const {
  setBhiRange,
  setBhiPreset,
  setBhiSelectedAccounts,
  setBhiAccountStatus,
  setBhiExceptionPreset,
  resetBhiFilters,
} = bhiDashboardSlice.actions;

export default bhiDashboardSlice.reducer;
